import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronUpLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { answerRetrievalCategoryResultsAtom } from "@mirage/mosaics/SearchPageDataRetrieval/searchPageDataRetrieval";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { contentTypeToSearchFilter } from "@mirage/shared/search/search-filters";
import { useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CategoryConfig } from "../CategorySearchAccordion/config";
import { useFilterSearch } from "../CategorySearchAccordion/useFilterSearch";
import { FilterRow } from "./FilterRow";
import styles from "./FilterRow.module.css";
import { filterableContentTypeIcons } from "./utils";

const RESPONSE_LENGTH_LIMIT = 100;
const DEFAULT_NUM_CONTENT_TYPES_SHOWN = 4;

type Props = {
  query: string;
  seeAllForFilter: (filter: SearchFilter) => void;
  categories: CategoryConfig[];
  hasResults?: () => void;
  shouldSkipCache?: boolean;
};

export const ContentTypeRows = ({
  query,
  seeAllForFilter,
  categories,
  shouldSkipCache,
}: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const moreOrLessRow = useMemo(() => {
    if (categories.length <= DEFAULT_NUM_CONTENT_TYPES_SHOWN) return null;
    const iconSrc = expanded ? ChevronUpLine : MoreHorizontalLine;
    return {
      title: expanded ? t("less") : t("more"),
      icon: <UIIcon className={styles.filterIcon} src={iconSrc} />,
      onClick: () => setExpanded(!expanded),
    };
  }, [expanded, categories.length]);
  const displayedCategories = useMemo(() => {
    if (expanded) return categories;
    return categories.slice(0, DEFAULT_NUM_CONTENT_TYPES_SHOWN);
  }, [expanded, categories]);
  return (
    <Box className={styles.filterGroup}>
      {displayedCategories.map((category) => {
        return (
          <ContentTypeRow
            key={category.id}
            query={query}
            category={category}
            seeAllForFilter={seeAllForFilter}
            shouldSkipCache={shouldSkipCache}
          />
        );
      })}
      {moreOrLessRow && <FilterRow {...moreOrLessRow} />}
    </Box>
  );
};

type ContentTypeRowProps = {
  query: string;
  category: CategoryConfig;
  seeAllForFilter: (filter: SearchFilter) => void;
  shouldSkipCache?: boolean;
};

export const ContentTypeRow = ({
  query,
  category,
  seeAllForFilter,
  shouldSkipCache,
}: ContentTypeRowProps) => {
  const { t } = useTranslation();
  const setCategoryResults = useSetAtom(answerRetrievalCategoryResultsAtom);
  const filter = useMemo(
    () => contentTypeToSearchFilter(category.contentType) || null,
    [category.contentType],
  );
  const { results, loading } = useFilterSearch(
    query,
    filter,
    null,
    shouldSkipCache,
  );
  const countLabel = useMemo(() => {
    if (loading) return "";
    return results.length === RESPONSE_LENGTH_LIMIT
      ? t("count_plus", {
          count: RESPONSE_LENGTH_LIMIT,
        })
      : `${results.length}`;
  }, [results.length, loading]);

  useEffect(() => {
    setCategoryResults((prev) => ({
      ...prev,
      [category.id]: results,
    }));
  }, [results]);

  const handleSeeAll = () => {
    if (!filter) return;
    seeAllForFilter(filter);
  };
  return (
    <FilterRow
      key={category.id}
      title={category.label}
      icon={
        <UIIcon
          className={styles.filterIcon}
          src={filterableContentTypeIcons[category.contentType.key]}
        />
      }
      countLabel={countLabel}
      onClick={handleSeeAll}
      loading={loading}
    />
  );
};
